
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Loader2, Tag, Filter, Gamepad2, Smartphone } from 'lucide-react';
import { Product, User } from '../types';
import { getProducts } from '../services/storage';
import { Skeleton } from './Skeleton';

interface ShopProps {
  currentUser: User | null;
  onBalanceUpdate: (user: User) => void;
  onProductSelect: (product: Product) => void;
}

type ParentCategory = 'Games' | 'Apps';

const Shop: React.FC<ShopProps> = ({ currentUser, onBalanceUpdate, onProductSelect }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Hierarchy Filtering
  const [activeParent, setActiveParent] = useState<ParentCategory>('Apps');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState<string[]>(['All']);

  useEffect(() => {
    let mounted = true;
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const data = await getProducts();
        if (mounted) {
          setProducts(data);
        }
      } catch (e) {
        console.warn("Product fetch error", e);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    fetchProducts();
    return () => { mounted = false; };
  }, []);

  // Update categories when Parent or Products change
  useEffect(() => {
      if (!products.length) return;
      
      const filteredByParent = products.filter(p => {
          // Robust parsing for "Root:::Category:::Product" or legacy "Root:::Category"
          const fullPath = p.category_path || p.category || 'Apps:::Uncategorized';
          const parts = fullPath.split(':::');
          const root = parts[0] || 'Apps';
          return root === activeParent;
      });

      const uniqueCats = Array.from(new Set(filteredByParent.map(p => {
          const fullPath = p.category_path || p.category || 'Apps:::Uncategorized';
          const parts = fullPath.split(':::');
          return parts[1] || 'Uncategorized';
      })));
      
      setCategories(['All', ...uniqueCats]);
      setSelectedCategory('All'); 
  }, [activeParent, products]);

  const handleBuyClick = (product: Product) => {
    if (isProcessing) return;
    setIsProcessing(true);
    onProductSelect(product);
    setTimeout(() => setIsProcessing(false), 500);
  };

  const filteredProducts = products.filter(p => {
      const fullPath = p.category_path || p.category || 'Apps:::Uncategorized';
      const parts = fullPath.split(':::');
      const root = parts[0] || 'Apps';
      const sub = parts[1] || 'Uncategorized';

      if (root !== activeParent) return false;
      if (selectedCategory !== 'All' && sub !== selectedCategory) return false;
      return true;
  });

  return (
    <div className="relative pb-10 animate-fade-in">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Marketplace</h2>
            <p className="text-zinc-500 text-xs font-mono mt-1">DIGITAL GOODS & ASSETS</p>
        </div>
      </div>

      {/* PARENT CATEGORY TOGGLE */}
      <div className="bg-zinc-900/50 p-1.5 rounded-2xl flex gap-2 border border-white/5 mb-6 relative overflow-hidden">
          {/* Active Background Slide Animation */}
          <motion.div 
             className="absolute top-1.5 bottom-1.5 bg-accent-500 rounded-xl z-0 shadow-neon"
             layoutId="parentToggle"
             initial={false}
             animate={{ 
                 left: activeParent === 'Games' ? '4px' : '50%',
                 width: 'calc(50% - 6px)',
                 x: activeParent === 'Apps' ? '4px' : '0%'
             }}
             transition={{ type: "spring", stiffness: 300, damping: 30 }}
          />

          <button
              onClick={() => setActiveParent('Games')}
              className={`flex-1 relative z-10 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm uppercase tracking-wider transition-colors ${activeParent === 'Games' ? 'text-black' : 'text-zinc-500 hover:text-white'}`}
          >
              <Gamepad2 size={18} /> Games
          </button>
          <button
              onClick={() => setActiveParent('Apps')}
              className={`flex-1 relative z-10 flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-sm uppercase tracking-wider transition-colors ${activeParent === 'Apps' ? 'text-black' : 'text-zinc-500 hover:text-white'}`}
          >
              <Smartphone size={18} /> Apps
          </button>
      </div>

      {/* SUB CATEGORY SCROLL */}
      <div className="sticky top-0 z-20 bg-bg-main/95 backdrop-blur-xl py-4 mb-6 -mx-4 px-4 md:mx-0 md:px-0 overflow-x-auto no-scrollbar border-b border-white/5">
        <div className="flex items-center gap-2">
           <Filter size={14} className="text-zinc-500 mr-2 shrink-0" />
           {categories.map(cat => (
               <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap border ${
                      selectedCategory === cat
                      ? 'bg-zinc-100 text-black border-white shadow-[0_0_15px_rgba(255,255,255,0.3)]'
                      : 'bg-zinc-900/50 text-zinc-500 border-white/5 hover:border-white/20 hover:text-white'
                  }`}
               >
                   {cat}
               </button>
           ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
        <AnimatePresence mode="popLayout">
          {isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-white/5 bg-zinc-900/20">
                <Skeleton className="h-40 w-full bg-zinc-800/50" />
                <div className="p-4 space-y-3">
                  <Skeleton className="h-4 w-3/4 bg-zinc-800/50" />
                  <Skeleton className="h-3 w-1/2 bg-zinc-800/50" />
                  <div className="flex justify-between items-end pt-2">
                     <Skeleton className="h-6 w-12 bg-zinc-800/50" />
                     <Skeleton className="h-8 w-16 bg-zinc-800/50 rounded-lg" />
                  </div>
                </div>
              </div>
            ))
          ) : (
            filteredProducts.map((product) => (
              <motion.div
                key={product.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="group relative glass-panel rounded-2xl overflow-hidden hover:border-accent-500/50 hover:shadow-neon transition-all duration-300 flex flex-col"
              >
                <div className="h-40 overflow-hidden relative shrink-0 bg-zinc-900">
                  <div className="absolute inset-0 bg-gradient-to-t from-bg-card via-transparent to-transparent z-10 opacity-60" />
                  <img 
                    src={product.image} 
                    alt={product.name}
                    loading="lazy" 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-3 left-3 z-20 flex gap-1">
                     <span className="bg-black/60 backdrop-blur border border-white/10 text-white text-[9px] font-bold px-2 py-1 rounded-md uppercase tracking-wider">
                       {product.sub_category || 'Item'}
                     </span>
                  </div>
                </div>
                
                <div className="p-4 relative z-20 flex-1 flex flex-col">
                  <h3 className="text-sm font-bold text-white truncate mb-1">{product.name}</h3>
                  <div className="mt-auto flex justify-between items-end pt-3">
                    <p className="text-lg font-bold text-accent-500 font-mono">
                      ${product.price.toFixed(2)}
                    </p>
                    <button
                        onClick={() => handleBuyClick(product)}
                        disabled={!currentUser || isProcessing}
                        className="w-8 h-8 md:w-auto md:h-auto md:px-4 md:py-2 rounded-lg bg-white text-black hover:bg-accent-500 font-bold text-xs uppercase transition-all active:scale-95 flex items-center justify-center gap-1 disabled:opacity-50"
                    >
                        <ShoppingBag size={14} /> <span className="hidden md:inline">Buy</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
        
        {!isLoading && filteredProducts.length === 0 && (
           <div className="col-span-full py-20 text-center text-zinc-500 border border-dashed border-white/10 rounded-3xl bg-white/5">
             <ShoppingBag size={48} className="mx-auto mb-4 opacity-30" />
             <p className="font-mono text-sm">NO INVENTORY IN {activeParent.toUpperCase()}.</p>
             <p className="text-xs text-zinc-600 mt-2">Try switching the main category.</p>
           </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
